import { create } from "zustand";

import { initialSessionState, storeSellerId } from "../seller-session";

// UI / session state for the talent SPA. Server state (the directory, a talent's
// thread list, a thread's detail) lives in the TanStack Query cache — see
// lib/queries/seller.ts — and the store NEVER holds a copy of it. This mirrors
// web's workspace-store split (server state in Query, UI/session in Zustand).
//
// `activeSellerId` is dual-use, exactly like web's `currentProjectId`: a component
// reads it via a selector to KEY the queries (switching talent re-keys the reads),
// and the Pusher dispatcher reads it via `getState()` to build the cache keys it
// writes — the two never drift because they read the same field.
/** One thread's PDF-export state. The render happens in the worker (doctrine
 *  1.1), so the button is a small state machine rather than a link:
 *  idle → rendering (POST accepted, awaiting `seller.export.ready`) → ready
 *  (the download URL arrived) | failed (the render soft-failed, or nothing
 *  arrived before the watchdog). `turnId` is what the incoming event is matched
 *  on — an event for a superseded attempt must not resolve the current one. */
export interface SellerExportState {
  phase: "rendering" | "ready" | "failed";
  turnId: string;
  url?: string;
  filename?: string;
  /** This turn's bytes have already been handed to the browser. Lives HERE, not
   *  in a component ref: the chip unmounts on a thread switch while this entry
   *  survives, so a per-mount latch would re-fire an unasked download on remount
   *  (the behaviour PR #692 removed from the buyer side). Reset by `startExport`,
   *  so a deliberate second click still downloads. */
  downloaded?: boolean;
}

interface SellerStore {
  /** The simulated talent's id. "" = no identity → the directory picker. */
  activeSellerId: string;
  /** The project open in the side panel, beside the stacked cards (null = none, so the
   *  page shows the cards alone). */
  selectedThreadId: string | null;
  /** Tester feedback toggle (red "Debug" pill). When ON, a floating note box
   *  lets the tester log debug notes (admin "Debug Notes" tab) from any view —
   *  the talent-side twin of the buyer app's toggle. Global tester tooling,
   *  persisted to localStorage so it survives a reload; NOT tied to a talent. */
  debugMode: boolean;
  /** In-flight / finished PDF exports, keyed by DOCUMENT (`exportKey`), not by
   *  thread: the brief and client-profile chips are separate renders, and one
   *  shared entry would spin both and download twice on one ready event.
   *  UI state, deliberately NOT persisted: a reload drops back to idle, and one
   *  click re-derives it (the server hands a finished render's URL straight back
   *  on the 202). */
  exports: Record<string, SellerExportState>;
  /** Ready results that arrived BEFORE anything was waiting for them, keyed by
   *  turn id.
   *
   *  The POST that binds a turn resolves after several server round trips, and
   *  the render it collapses onto may already be nearly done — so
   *  `seller.export.ready` can land in the gap between the SPA subscribing and
   *  `startExport` recording which turn it cares about. The worker's publish is
   *  seq=1-guarded and never fires again, so an event dropped in that gap is
   *  gone for good and the button spins to the watchdog. Buffering it here means
   *  `startExport` can find it waiting and resolve immediately.
   *
   *  Bounded: entries are consumed on use and the map is capped, so a session
   *  that never claims one cannot grow it without limit. */
  pendingExportResults: Record<
    string,
    { ok: boolean; url?: string | null; filename?: string | null }
  >;
  /** The preferences drawer (slides in from the left) is open. Overlay UI state,
   *  deliberately not in the URL or persisted: a reload lands on the page, not on a
   *  half-edited form. */
  prefsOpen: boolean;
  /** The Pro insights view is open. Overlay UI state like `prefsOpen`. */
  insightsOpen: boolean;
  setPrefsOpen: (open: boolean) => void;
  setInsightsOpen: (open: boolean) => void;

  /** Switch identity: persist it and clear the open thread (one atomic action). */
  setActiveSeller: (sellerId: string) => void;
  /** Open a specific talent's thread directly (the project-first pick): set the
   *  identity AND the open thread atomically, landing straight in the chat. */
  openSellerThread: (sellerId: string, threadId: string) => void;
  /** Clear identity → back to the picker (the "Browse all talents" path). */
  clearActiveSeller: () => void;
  setSelectedThread: (threadId: string | null) => void;
  setDebugMode: (on: boolean) => void;
  /** Mark a document's export as started (or restarted) on ``turnId`` — or
   *  resolve it outright if that turn's result already arrived while nothing was
   *  bound to it (see `pendingExportResults`). */
  startExport: (key: string, turnId: string) => void;
  /** Resolve an export from a ``seller.export.ready`` event, addressed by TURN.
   *
   *  The turn identifies the render uniquely, which is why the event needs no
   *  document discriminator to route correctly: the store finds whichever entry
   *  is waiting on it. Ignores a turn no entry is waiting on any more (a
   *  superseded attempt), and never downgrades a READY export back to failed —
   *  the worker's success rides seq=1 and its soft-fail advisory seq=2, so the
   *  two can arrive in either order. */
  resolveExport: (
    turnId: string,
    result: { ok: boolean; url?: string | null; filename?: string | null },
  ) => void;
  /** Record that this turn's file has been handed to the browser, so a remount
   *  of the chip does not download it again. No-op unless the entry is still the
   *  READY one for `turnId`. */
  markExportDownloaded: (key: string, turnId: string) => void;
  /** Give up on an export the watchdog outlived (no event ever arrived). */
  failExport: (key: string, turnId: string) => void;
  /** The POST itself failed, so no turn exists to key a failure on. Refuses to
   *  clobber a `rendering` entry (that render is still coming) or a `ready` one
   *  (its PDF exists). */
  failExportEnqueue: (key: string) => void;
}

// Debug-mode persistence. A GLOBAL tester setting (not per-talent), so it lives
// in localStorage — survives reloads, seeded into the store on creation. Same
// key the buyer app uses (a separate origin, so the stores are independent).
const DEBUG_MODE_KEY = "recruiter_debug_mode";

function getStoredDebugMode(): boolean {
  try {
    return window.localStorage?.getItem(DEBUG_MODE_KEY) === "1";
  } catch {
    return false;
  }
}

function storeDebugMode(on: boolean): void {
  try {
    if (on) window.localStorage?.setItem(DEBUG_MODE_KEY, "1");
    else window.localStorage?.removeItem(DEBUG_MODE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — debug mode is best-effort. */
  }
}

// An unclaimed export result is only ever waiting for the POST that binds its
// turn, which either resolves in seconds or failed — so a handful is generous
// and an unbounded map would be a leak in a long tester session.
const MAX_PENDING_EXPORT_RESULTS = 8;

type PendingExportResult = { ok: boolean; url?: string | null; filename?: string | null };

/** The one place a result becomes a terminal phase. An `ok` with no `url` is a
 *  FAILURE, not a ready state with nothing to fetch: `url` is optional on the
 *  wire and a ready phase without one would fire a download at `undefined`. */
function settle(turnId: string, result: PendingExportResult): SellerExportState {
  return result.ok && result.url
    ? { phase: "ready", turnId, url: result.url, filename: result.filename ?? undefined }
    : { phase: "failed", turnId };
}

/** Store key for one DOCUMENT's export — a thread can show several chips. */
export function exportKey(threadId: string, kind: string): string {
  return `${threadId}:${kind}`;
}

function capPending(
  current: Record<string, PendingExportResult>,
  turnId: string,
  result: PendingExportResult,
): Record<string, PendingExportResult> {
  const next = { ...current, [turnId]: result };
  const keys = Object.keys(next);
  if (keys.length <= MAX_PENDING_EXPORT_RESULTS) return next;
  // Object key order is insertion order for string keys, so the front is oldest.
  for (const stale of keys.slice(0, keys.length - MAX_PENDING_EXPORT_RESULTS)) {
    delete next[stale];
  }
  return next;
}

// Boot state is URL-first (deep links win over the persisted identity) — see
// initialSessionState. Module-scope, runs once; App.tsx keeps URL and store in
// sync from here on (lib/route-url.ts).
const boot = initialSessionState();

export const useSellerStore = create<SellerStore>((set) => ({
  activeSellerId: boot.sellerId,
  selectedThreadId: boot.threadId,
  debugMode: getStoredDebugMode(),
  exports: {},
  pendingExportResults: {},
  prefsOpen: false,
  insightsOpen: false,
  setPrefsOpen: (prefsOpen) => set({ prefsOpen }),
  setInsightsOpen: (insightsOpen) => set({ insightsOpen }),

  setActiveSeller: (sellerId) => {
    // Persist inline (mirrors web's syncWorkspace calling storeActiveProjectId) so a
    // reload restores the same simulated talent — no separate effect to order.
    storeSellerId(sellerId);
    set({
      activeSellerId: sellerId,
      selectedThreadId: null,
      prefsOpen: false,
      insightsOpen: false,
    });
  },
  openSellerThread: (sellerId, threadId) => {
    // Persist the identity (same as setActiveSeller) but keep the chosen thread
    // open so a project-first pick lands directly in the conversation.
    storeSellerId(sellerId);
    set({ activeSellerId: sellerId, selectedThreadId: threadId });
  },
  clearActiveSeller: () => {
    storeSellerId("");
    set({ activeSellerId: "", selectedThreadId: null, prefsOpen: false, insightsOpen: false });
  },
  setSelectedThread: (selectedThreadId) => set({ selectedThreadId }),
  setDebugMode: (on) => {
    storeDebugMode(on);
    set({ debugMode: on });
  },
  startExport: (key, turnId) =>
    set((s) => {
      const early = s.pendingExportResults[turnId];
      if (!early) {
        return { exports: { ...s.exports, [key]: { phase: "rendering", turnId } } };
      }
      // Its result beat us here. Consume the buffered entry and land on the
      // terminal phase directly, so the button never waits for an event that
      // has already been and gone.
      const { [turnId]: _consumed, ...rest } = s.pendingExportResults;
      return {
        pendingExportResults: rest,
        exports: { ...s.exports, [key]: settle(turnId, early) },
      };
    }),
  resolveExport: (turnId, result) =>
    set((s) => {
      const key = Object.keys(s.exports).find((k) => s.exports[k]?.turnId === turnId);
      // No entry is waiting on this turn. Either the event outran the POST that
      // would have bound it — park the result, `startExport` collects it — or
      // the attempt was superseded, in which case the parked entry is never
      // claimed and ages out of the capped buffer.
      if (key === undefined) {
        return { pendingExportResults: capPending(s.pendingExportResults, turnId, result) };
      }
      // seq=1 (success) may land after seq=2 (the soft-fail advisory), so a
      // ready export is terminal: a later failure never overwrites a real URL.
      if (s.exports[key]?.phase === "ready") return s;
      return { exports: { ...s.exports, [key]: settle(turnId, result) } };
    }),
  markExportDownloaded: (key, turnId) =>
    set((s) => {
      const current = s.exports[key];
      if (!current || current.turnId !== turnId || current.phase !== "ready") return s;
      if (current.downloaded) return s;
      return { exports: { ...s.exports, [key]: { ...current, downloaded: true } } };
    }),
  failExport: (key, turnId) =>
    set((s) => {
      const current = s.exports[key];
      if (!current || current.turnId !== turnId || current.phase !== "rendering") return s;
      return { exports: { ...s.exports, [key]: { phase: "failed", turnId } } };
    }),
  failExportEnqueue: (key) =>
    set((s) => {
      const phase = s.exports[key]?.phase;
      // Never clobber an entry that still holds something real: `rendering` is a
      // render still coming, and `ready` holds the URL of a PDF that EXISTS. The
      // caller surfaces the enqueue error itself; the store keeps the artifact.
      if (phase === "rendering" || phase === "ready") return s;
      return { exports: { ...s.exports, [key]: { phase: "failed", turnId: "" } } };
    }),
}));
